import { useState, useEffect, useRef, useCallback } from 'react'
import { INDEX_PANEL_SEARCH_EVENT } from '@/lib/index-surface-actions'

export type UsePanelSearchOptions = {
  /** When false, listeners are not attached and open state is cleared. */
  enabled?: boolean
  /** Seed from a share-link `q` (or last-used persist). */
  initialQuery?: string
}

export function usePanelSearch(options: UsePanelSearchOptions = {}) {
  const { enabled = true, initialQuery = '' } = options
  const [searchOpen, setSearchOpen] = useState(() => Boolean(initialQuery.trim()))
  const [searchQuery, setSearchQuery] = useState(() => initialQuery)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!enabled) {
      setSearchOpen(false)
      setSearchQuery('')
    }
  }, [enabled])

  useEffect(() => {
    if (!enabled) return
    const onTitleRowSearch = () => {
      setSearchOpen(true)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
    window.addEventListener(INDEX_PANEL_SEARCH_EVENT, onTitleRowSearch)
    return () => window.removeEventListener(INDEX_PANEL_SEARCH_EVENT, onTitleRowSearch)
  }, [enabled])

  useEffect(() => {
    if (!enabled) return

    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      const inInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      if (inInput && !inputRef.current?.contains(target)) return

      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        setSearchOpen(true)
        setTimeout(() => inputRef.current?.focus(), 50)
      }
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false)
        setSearchQuery('')
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [enabled, searchOpen])

  const open = useCallback(() => {
    if (!enabled) return
    setSearchOpen(true)
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [enabled])

  const close = useCallback(() => {
    setSearchOpen(false)
    setSearchQuery('')
  }, [])

  return { searchOpen, searchQuery, setSearchQuery, inputRef, open, close }
}
